import React, { useState } from 'react';
import { Box, Tooltip, Typography } from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import { getMemberMemos } from '../../../api/user';
import { useTranslation } from 'react-i18next';
import moment from 'moment';
import useUserAuth from '../../../utils/useUserAuth';
import {
  UserInfoWrapper,
  UserInfoCard,
  UserInfoLeft,
  UserInfoTopRow,
  UserInfoNameGroup,
  UserInfoName,
  UserInfoMetaRow,
  UserInfoMeta,
  UserInfoLabel,
  UserInfoText,
  UserInfoGender,
  UserInfoStats,
  SaleButton,
  SaleButtonIcon,
  MemoPreviewCard,
  MemoPreviewText,
  MemoPreviewPlaceholder,
  MemoEditButton,
} from './UserDetailStyled';
import { StatCard, StatLabel, StatValue } from '../UserGeneralTab/UserGeneralTabStyled';
import MemoEditModal from './MemoEditModal';

// 만료/임박 강조(red 카드) 기준 — 운영 피드백에 따라 이 두 값만 조정하면 된다.
const EXPIRY_WARNING_DAYS = 30; // 만료일이 오늘부터 N일 이내(또는 이미 지남)면 강조
const REMAINING_COUNT_WARNING = 3; // 잔여 횟수가 N회 이하면 강조

const getGenderKorean = gender => {
  if (gender === 'MALE') return '남성';
  if (gender === 'FEMALE') return '여성';
  return '미지정';
};

// 만료일이 이미 지났거나 EXPIRY_WARNING_DAYS 이내로 남았는지.
const isExpiryCritical = date => {
  if (!date) return false;
  const target = moment(date, 'YYYY-MM-DD');
  if (!target.isValid()) return false;
  return target.diff(moment().startOf('day'), 'days') <= EXPIRY_WARNING_DAYS;
};

const isCountCritical = count => count != null && Number(count) <= REMAINING_COUNT_WARNING;

// 라벨 + 값 한 쌍을 카드 형태로. danger면 red 톤.
const Stat = ({ label, value, danger }) => (
  <StatCard tone={danger ? 'danger' : undefined}>
    <StatLabel>{label}</StatLabel>
    <StatValue tone={danger ? 'danger' : undefined}>{value ?? '-'}</StatValue>
  </StatCard>
);

const UserDetailUserInfo = ({ user, userInfo, userBranchId, token, onSaleClick }) => {
  const { t } = useTranslation();
  const canSaleRegister = useUserAuth('MEMBER_SALE_REGISTER');
  const [memoModalOpen, setMemoModalOpen] = useState(false);
  const displayName = user?.fullname || user?.name || '-';
  const memberId = user?.memberId;

  const { data: memos = [] } = useQuery({
    queryKey: ['memberMemos', memberId, userBranchId],
    queryFn: () => getMemberMemos(memberId, userBranchId, token),
    enabled: !!memberId && !!userBranchId && !!token,
  });
  const latestMemo = memos.length > 0 ? memos[0] : null;

  return (
    <UserInfoWrapper>
      <UserInfoCard>
        <UserInfoLeft>
          <UserInfoTopRow>
            <UserInfoNameGroup>
              <UserInfoName>{displayName}</UserInfoName>
              <UserInfoGender gender={user?.gender}>{getGenderKorean(user?.gender)}</UserInfoGender>
            </UserInfoNameGroup>
          </UserInfoTopRow>

          <UserInfoMetaRow>
            {!!user?.age && (
              <UserInfoMeta>
                <UserInfoLabel>나이</UserInfoLabel>
                <UserInfoText>{user.age}세</UserInfoText>
              </UserInfoMeta>
            )}
            <UserInfoMeta>
              <UserInfoLabel>연락처</UserInfoLabel>
              <UserInfoText>
                {user?.phoneNumber || '-'}
              </UserInfoText>
            </UserInfoMeta>
          </UserInfoMetaRow>
        </UserInfoLeft>

        <Tooltip title={!canSaleRegister ? '권한이 없습니다' : ''} arrow>
          <SaleButton type="button" onClick={onSaleClick} disabled={!canSaleRegister}>
            <SaleButtonIcon src="/images/productTicket.png" alt="상품판매" />
            상품판매
          </SaleButton>
        </Tooltip>
      </UserInfoCard>

      <MemoPreviewCard>
        {latestMemo?.memo ? (
          <MemoPreviewText>{latestMemo.memo}</MemoPreviewText>
        ) : (
          <MemoPreviewPlaceholder>{t('메모를 입력해 주세요.')}</MemoPreviewPlaceholder>
        )}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px', mt: '4px' }}>
          <Typography sx={{ fontSize: '11px', color: '#aaa' }}>
            {latestMemo?.createdByName || '-'} / {latestMemo?.createdAt ? latestMemo.createdAt.replace(/-/g, '.').slice(0, 16) : '-'}
          </Typography>
          <MemoEditButton sx={{ ml: 'auto' }} onClick={() => setMemoModalOpen(true)}>{t('수정하기')}</MemoEditButton>
        </Box>
      </MemoPreviewCard>

      <UserInfoStats>
        <Stat
          label={t('수강권 잔여 수')}
          value={userInfo?.ticketTotalRemainingCount}
          danger={isCountCritical(userInfo?.ticketTotalRemainingCount)}
        />
        <Stat
          label={t('수강 만료일')}
          value={userInfo?.ticketExpireDate}
          danger={isExpiryCritical(userInfo?.ticketExpireDate)}
        />
        <Stat
          label={t('라커 만료일')}
          value={userInfo?.lockerExpireDate}
          danger={isExpiryCritical(userInfo?.lockerExpireDate)}
        />
      </UserInfoStats>

      <MemoEditModal
        open={memoModalOpen}
        onClose={() => setMemoModalOpen(false)}
        memberId={user?.memberId}
        branchId={userBranchId}
        token={token}
      />
    </UserInfoWrapper>
  );
};

export default UserDetailUserInfo;
